import { useState, MouseEvent } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Button,
  makeStyles,
  Grid,
  ButtonGroup,
  ListItem,
  List,
  ListItemText,
  Typography,
  Box,
  LinearProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import grey from '@material-ui/core/colors/grey';
import { adServiceApiRef } from '../../services/ad.service';
import { useRunwayClusters } from '../../hooks';
import { useAsync } from 'react-use';
import { filterClusters, hasPciCluster } from './cluster-filters';
import { DataClassification, Environment, SelectorCluster, SelectorView, UserGroup } from './types';

export const clusterSelectorStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    overflow: 'auto',
    maxHeight: 210,

    '&$selected': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
    },
  },
  listSection: {
    backgroundColor: 'inherit',
  },
  ul: {
    backgroundColor: 'inherit',
    padding: 0,
  },
  selected: {},
  gridContainer: {
    maxWidth: 900,
  },
  formGroup: {
    padding: theme.spacing(2),
  },
  button: {
    marginRight: theme.spacing(1),
  },
  buttonGroup: {
    margin: '1rem 0',
  },
  section: {
    margin: '1rem 0',
  },
  nested: {
    paddingLeft: theme.spacing(4),
    paddingTop: 0,
    paddingBottom: 0,
    display: 'list-item',
    listStyleType: 'disc',
    listStylePosition: 'inside',
  },
  flexContainer: {
    display: 'block',
    flexDirection: 'row',
    padding: 5,
    margin: 0,
  },
  listItem: {
    padding: 0,
    margin: 0,
    display: 'list-item',
    listStyleType: 'disc',
    listStylePosition: 'inside',
  },
  smallText: {
    fontSize: '0.9em',
  },
  container: {
    padding: '1rem',
  },
}));

type ClusterSelectorProps = {
  onClick: (cluster: string) => void;
  includeProd?: boolean;
  cluster?: string | null;
};

export const ClusterSelector = ({ onClick, includeProd = true, cluster = null }: ClusterSelectorProps) => {
  const classes = clusterSelectorStyles();
  const [clusterSelectionMethod, setClusterSelectionMethod] = useState<string>(SelectorView.guided);
  const [clusterEnv, setClusterEnv] = useState<string>(Environment.nonprod);
  const [clusterDataClassification, setClusterDataClassification] = useState<string>(DataClassification.nonpci);
  const [selectedCluster, setSelectedCluster] = useState<string | null>(cluster);
  const [usersAdGroups, setUsersAdGroups] = useState<UserGroup[]>([]);
  const ad = useApi(adServiceApiRef);

  const getUsersGroups = useAsync(async () => {
    const resp: any = await ad.getUsersGroupMembershipAzureAD();
    setUsersAdGroups(resp?.outputGroups);
  });

  const getClusters = useRunwayClusters();

  const clusters = getClusters.value ?? [];
  const visibleClusters = filterClusters(
    clusters,
    clusterEnv,
    clusterDataClassification,
    clusterSelectionMethod,
    includeProd,
    usersAdGroups,
  );

  const handleSelectionMethod = (selectionMethod: string) => {
    setClusterSelectionMethod(selectionMethod);
    setSelectedCluster(null);
    onClick('');
  };
  const handleEnvSelection = (env: string) => {
    setClusterEnv(env);
    onClick('');
  };
  const handleDataClassificationSelection = (dataClassification: string) => {
    setClusterDataClassification(dataClassification);
    onClick('');
  };

  const handleListItemClick = (_event: MouseEvent, newCluster: string) => {
    setSelectedCluster(newCluster);
    onClick(newCluster);
  };

  return (
    <Grid container spacing={3}>
      <ButtonGroup
        fullWidth
        variant="contained"
        aria-label="full width outlined button group"
        classes={{ root: classes.buttonGroup }}
      >
        <Button
          color={clusterSelectionMethod === SelectorView.guided ? 'primary' : 'inherit'}
          onClick={() => handleSelectionMethod(SelectorView.guided)}
        >
          Guided
        </Button>
        <Button
          color={clusterSelectionMethod === SelectorView.advanced ? 'primary' : 'inherit'}
          onClick={() => handleSelectionMethod(SelectorView.advanced)}
        >
          Advanced
        </Button>
      </ButtonGroup>
      {!getUsersGroups.loading && !getClusters.loading && clusterSelectionMethod === SelectorView.guided && (
        <>
          {includeProd && (
            <ButtonGroup
              fullWidth
              variant="contained"
              aria-label="full width outlined button group"
              classes={{ root: classes.buttonGroup }}
            >
              <Button
                color={clusterEnv === Environment.nonprod ? 'primary' : 'inherit'}
                onClick={() => handleEnvSelection(Environment.nonprod)}
              >
                Non-Production
              </Button>
              <Button
                disabled={!includeProd}
                color={clusterEnv === Environment.prod ? 'primary' : 'inherit'}
                onClick={() => handleEnvSelection(Environment.prod)}
              >
                Production
              </Button>
            </ButtonGroup>
          )}
          <ButtonGroup
            fullWidth
            variant="contained"
            aria-label="full width outlined button group"
            classes={{ root: classes.buttonGroup }}
          >
            <Button
              color={clusterDataClassification === DataClassification.nonpci ? 'primary' : 'inherit'}
              onClick={() => handleDataClassificationSelection(DataClassification.nonpci)}
            >
              Non-PCI
            </Button>
            <Button
              disabled={!hasPciCluster(clusters)}
              color={clusterDataClassification === DataClassification.pci ? 'primary' : 'inherit'}
              onClick={() => handleDataClassificationSelection(DataClassification.pci)}
            >
              PCI
            </Button>
          </ButtonGroup>
        </>
      )}
      {(getUsersGroups.loading || getClusters.loading) && (
        <LinearProgress
          style={{
            marginTop: 30,
            marginLeft: 10,
            marginRight: 10,
            width: '100%',
          }}
        />
      )}
      {!getUsersGroups.loading && !getClusters.loading && getClusters.error && (
        <Grid item xs={12}>
          <Alert severity="error">Unable to load cluster metadata</Alert>
        </Grid>
      )}
      {!getUsersGroups.loading && !getClusters.loading && !getClusters.error && visibleClusters.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom align="left">
            Select a Cluster
          </Typography>
          <Box borderRadius="borderRadius" border={1} borderColor={grey[400]} p="2" m="2">
            <List subheader={<li />}>
              {visibleClusters.map((item: SelectorCluster) => {
                const displayName = item.displayName ? item.displayName : item.name;
                return (
                  <ListItem
                    disabled={!item.deployable}
                    button
                    key={`${item.name}-list-item`}
                    onClick={event => {
                      handleListItemClick(event, `${item.name}`);
                    }}
                    selected={cluster ? cluster === `${item.name}` : selectedCluster === `${item.name}`}
                    classes={{
                      root: classes.root,
                      selected: classes.selected,
                    }}
                  >
                    <ListItemText
                      primary={`${item.deployable ? displayName : `${displayName} - NOT DEPLOYABLE`}`}
                      key={`${item.name}-list-text`}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Grid>
      )}
      {!getUsersGroups.loading && !getClusters.loading && !getClusters.error && visibleClusters.length === 0 && (
        <Grid item xs={12}>
          <Alert severity="error">No Clusters Found</Alert>
        </Grid>
      )}
    </Grid>
  );
};
